// Hand-ported from LiveKit's Agents UI shadcn registry
// (@agents-ui/agent-chat-indicator, https://livekit.com/ui/r/agent-chat-indicator.json,
//  livekit/components-js).
//
// Upstream animates a single dot with the `motion` package (opacity 0.5→1,
// scale 1→1.2, spring, infinite mirror). We reproduce the same pulse with a CSS
// keyframe (`agent-chat-indicator-pulse` in index.css) instead — it avoids a
// framer-motion/motion dependency in the hot path and, crucially, doesn't break
// in test suites that mock `framer-motion` (several conversation tests do).
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const agentChatIndicatorVariants = cva(
  'bg-muted-foreground inline-block size-2.5 rounded-full motion-safe:animate-[agent-chat-indicator-pulse_0.5s_ease-in-out_infinite_alternate]',
  {
    variants: {
      size: {
        sm: 'size-2.5',
        md: 'size-4',
        lg: 'size-6',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface AgentChatIndicatorProps
  extends ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof agentChatIndicatorVariants> {
  size?: 'sm' | 'md' | 'lg';
}

export const AgentChatIndicator = forwardRef<HTMLSpanElement, AgentChatIndicatorProps>(
  function AgentChatIndicator({ size = 'md', className, ...props }, ref) {
    return (
      <span
        ref={ref}
        className={cn(agentChatIndicatorVariants({ size }), className)}
        {...props}
      />
    );
  },
);
